import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import { toRSD, todayStr } from '../lib/helpers';
import Icon from './Icon';

// ============================================================
// STATUS FAKTURE — U pripremi / Poslato / Naplaćeno / Stornirano / Delimično
// ============================================================

export const STATUS_KEYS = ['u_pripremi', 'poslato', 'naplaceno', 'stornirano', 'delimicno'];

// Stare vrednosti iz baze (pre uvođenja statusa)
export const normalizeStatus = (s) => {
  if (s === 'nacrt') return 'u_pripremi';
  if (!s || s === 'izdata') return 'poslato';
  return STATUS_KEYS.includes(s) ? s : 'poslato';
};

export function statusMeta(t) {
  return {
    // Biznis stil: neutralna podloga i tekst, boja samo u tački
    u_pripremi: { label: t('stDraft'), bg: 'var(--paper)', fg: 'var(--ink-700)', dot: 'var(--st-draft)' },
    poslato: { label: t('stSent'), bg: 'var(--paper)', fg: 'var(--ink-700)', dot: 'var(--st-sent)' },
    naplaceno: { label: t('stPaid'), bg: 'var(--paper)', fg: 'var(--ink-700)', dot: 'var(--st-paid)' },
    stornirano: { label: t('stCancelled'), bg: 'var(--paper)', fg: 'var(--ink-700)', dot: 'var(--st-cancel)' },
    delimicno: { label: t('stPartial'), bg: 'var(--paper)', fg: 'var(--ink-700)', dot: 'var(--st-partial)' },
  };
}

// Koliko je naplaćeno, preračunato u dinare
export function paidRSD(inv) {
  const st = normalizeStatus(inv.status);
  if (st === 'naplaceno') return toRSD(inv);
  if (st === 'delimicno') {
    const rate = inv.type === 'domaca' || inv.document_kind === 'fiskalni' ? 1 : (inv.exchange_rate || 1);
    return Math.min(Number(inv.paid_amount) || 0, Number(inv.amount) || 0) * rate;
  }
  return 0;
}

export async function updateInvoiceStatus(inv, status, paidAmount) {
  const patch = { status, paid_amount: 0, paid_at: null };
  if (status === 'naplaceno') { patch.paid_amount = inv.amount; patch.paid_at = todayStr(); }
  if (status === 'delimicno') { patch.paid_amount = paidAmount; patch.paid_at = todayStr(); }
  return supabase.from('invoices').update(patch).eq('id', inv.id).select().single();
}

export function StatusBadge({ status }) {
  const { t } = useI18n();
  const m = statusMeta(t)[normalizeStatus(status)];
  return (
    <span style={{ ...badge, background: m.bg, color: m.fg }}>
      <span style={{ ...dot, background: m.dot }} />
      {m.label}
    </span>
  );
}

// ============================================================
// UNOS UPLATE — iznos, datum uplate (bitan za limit od 8 miliona), način i beleška
// ============================================================
const PAY_METHODS = [
  ['prenos', 'Prenos na račun', 'Bank transfer', 'Банковский перевод'],
  ['gotovina', 'Gotovina', 'Cash', 'Наличные'],
  ['kartica', 'Kartica', 'Card', 'Карта'],
  ['drugo', 'Drugo', 'Other', 'Другое'],
];

const fmtMoney = (v) => (Number(v) || 0).toLocaleString('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PaymentDialog({ invoice, onClose, onSaved }) {
  const { tr } = useI18n();
  const st = normalizeStatus(invoice.status);
  const cur = invoice.type === 'domaca' || invoice.document_kind === 'fiskalni' ? 'RSD' : (invoice.currency || 'RSD');
  const total = Number(invoice.amount) || 0;
  const paidBefore = st === 'delimicno' ? (Number(invoice.paid_amount) || 0) : 0;
  const due = Math.max(0, Math.round((total - paidBefore) * 100) / 100);
  const [amount, setAmount] = useState(due ? String(due) : '');
  const [date, setDate] = useState(todayStr());
  const [method, setMethod] = useState('prenos');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const v = parseFloat(String(amount).replace(',', '.')) || 0;
  const newPaid = Math.round((paidBefore + v) * 100) / 100;
  const left = Math.max(0, Math.round((total - newPaid) * 100) / 100);
  const full = v > 0 && newPaid >= total - 0.004;

  const save = async () => {
    setError('');
    if (!(v > 0)) { setError(tr('Unesite iznos uplate.', 'Enter the payment amount.', 'Введите сумму оплаты.')); return; }
    if (!date) { setError(tr('Unesite datum uplate.', 'Enter the payment date.', 'Введите дату оплаты.')); return; }
    setBusy(true);
    const patch = {
      status: full ? 'naplaceno' : 'delimicno',
      paid_amount: full ? total : newPaid,
      paid_at: date,
      payment_method: method,
      payment_note: note.trim() || null,
    };
    let { data, error: err } = await supabase.from('invoices').update(patch).eq('id', invoice.id).select().single();
    // Ako u bazi još nema kolona za način uplate i belešku, sačuvaj bez njih
    if (err && /payment_method|payment_note/.test(err.message || '')) {
      const { payment_method, payment_note, ...rest } = patch; // eslint-disable-line no-unused-vars
      ({ data, error: err } = await supabase.from('invoices').update(rest).eq('id', invoice.id).select().single());
    }
    setBusy(false);
    if (err) { setError(err.message); return; }
    if (onSaved && data) onSaved(data);
  };

  // Zatvaranje samo na ✕, "Otkaži" ili Esc — klik pored prozora ga NE zatvara (da se ne izgubi uneto)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div style={pd.overlay} className="no-print">
      <div style={pd.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div style={pd.titleRow}>
          <span style={pd.title}>{tr('Unesi uplatu', 'Record payment', 'Внести оплату')}</span>
          <button onClick={onClose} disabled={busy} style={pd.close} aria-label={tr('Zatvori', 'Close', 'Закрыть')}><Icon name="x" size={18} /></button>
        </div>
        <div style={pd.docRow}>
          <span style={pd.docNo}>{tr('Faktura', 'Invoice', 'Счёт')} {invoice.invoice_number}</span>
          <span style={pd.curTag}>{cur}</span>
          {invoice.client && <span style={pd.client}>· {invoice.client}</span>}
        </div>

        <div style={pd.grid} className="pd-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={pd.field}>
              <span style={pd.label}>{tr(`Iznos uplate (${cur})`, `Amount (${cur})`, `Сумма (${cur})`)}</span>
              <input autoFocus type="number" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} style={pd.input} />
            </label>
            <label style={pd.field}>
              <span style={pd.label}>{tr('Datum uplate *', 'Payment date *', 'Дата оплаты *')}</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={pd.input} />
              <span style={pd.hint}>{tr('Po ovom datumu se računa limit od 8 miliona.', 'The 8 million limit is counted by this date.', 'Лимит 8 млн считается по этой дате.')}</span>
            </label>
          </div>
          <div style={pd.summary}>
            <span style={pd.sumTitle}>{tr('PREGLED', 'SUMMARY', 'ИТОГ')}</span>
            <span style={pd.sumRow}><span>{tr('Iznos fakture', 'Invoice total', 'Сумма счёта')}</span><strong>{fmtMoney(total)}</strong></span>
            <span style={pd.sumRow}><span>{tr('Ranije plaćeno', 'Paid before', 'Оплачено ранее')}</span><strong>{fmtMoney(paidBefore)}</strong></span>
            <span style={pd.sumRow}><span>{tr('Ova uplata', 'This payment', 'Этот платёж')}</span><strong>{fmtMoney(v)}</strong></span>
            <span style={pd.sumLine} />
            <span style={pd.sumRow}>
              <span>{tr('Preostalo', 'Remaining', 'Осталось')}</span>
              <strong style={{ color: full ? 'var(--forest-600)' : 'var(--gold-600)' }}>
                {fmtMoney(left)} · {full ? tr('Naplaćeno', 'Paid', 'Оплачено') : tr('Delimično', 'Partial', 'Частично')}
              </strong>
            </span>
          </div>
        </div>

        <label style={pd.field}>
          <span style={pd.label}>{tr('Način uplate', 'Payment method', 'Способ оплаты')}</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)} style={pd.input}>
            {PAY_METHODS.map(([k, sr, en, ru]) => <option key={k} value={k}>{tr(sr, en, ru)}</option>)}
          </select>
        </label>
        <label style={pd.field}>
          <span style={pd.label}>{tr('Beleška', 'Note', 'Заметка')} <span style={{ fontWeight: 500, color: 'var(--ink-500)' }}>({tr('nije obavezno', 'optional', 'необязательно')})</span></span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={tr('Broj izvoda, poziv na broj…', 'Statement no., reference…', 'Номер выписки…')} style={{ ...pd.input, resize: 'vertical', minHeight: 56, fontFamily: 'inherit' }} />
        </label>

        {error && <div style={errBox}>{error}</div>}
        <div style={pd.btnRow}>
          <button onClick={onClose} disabled={busy} style={pd.cancel}>{tr('Otkaži', 'Cancel', 'Отмена')}</button>
          <button onClick={save} disabled={busy} style={pd.save}>{busy ? '...' : tr('Sačuvaj uplatu', 'Save payment', 'Сохранить')}</button>
        </div>
        <style>{`@media (max-width: 560px) { .pd-grid { grid-template-columns: 1fr !important; } }`}</style>
      </div>
    </div>
  );
}

// ============================================================
// Dugme ✓ (unos uplate) + meni ⋮ sa radnjama
// ============================================================
export function StatusActions({ invoice, onChange }) {
  const { t, tr } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('actions'); // actions | status
  const [paying, setPaying] = useState(false);
  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const meta = statusMeta(t);
  const st = normalizeStatus(invoice.status);
  const isProforma = invoice.document_kind === 'profaktura';
  const canPay = !isProforma && st !== 'naplaceno' && st !== 'stornirano';
  const canStorno = !isProforma && st !== 'stornirano';
  const cur = invoice.type === 'domaca' || invoice.document_kind === 'fiskalni' ? 'RSD' : invoice.currency;
  const base = `/fakturisanje/${invoice.id}`;

  const apply = async (status, paid) => {
    setError('');
    setBusy(true);
    const { data, error: err } = await updateInvoiceStatus(invoice, status, paid);
    setBusy(false);
    if (err) { setError(err.message); return; }
    setOpen(false);
    setPartial(false);
    setAmount('');
    if (onChange && data) onChange(data);
  };

  const pick = (key) => {
    if (key === 'delimicno') { setPartial(true); setAmount(invoice.paid_amount ? String(invoice.paid_amount) : ''); return; }
    apply(key);
  };

  const savePartial = () => {
    const v = parseFloat(String(amount).replace(',', '.'));
    if (!(v > 0) || v >= Number(invoice.amount)) { setError(`0 < iznos < ${invoice.amount}`); return; }
    apply('delimicno', v);
  };

  const openMenu = () => {
    // Meni se pozicionira uz dugme (fixed), da ga tabela sa skrolom ne odseče
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const top = Math.min(r.bottom + 6, window.innerHeight - 420);
      setPos({ top: Math.max(8, top), left: Math.max(8, Math.min(r.right - 240, window.innerWidth - 248)) });
    }
    setView('actions'); setPartial(false); setError('');
    setOpen((o) => !o);
  };

  const go = (to) => { setOpen(false); navigate(to); };
  const startPay = () => { setOpen(false); setPaying(true); };

  const actions = [
    { icon: 'receipt', label: tr('Pregled', 'View', 'Просмотр'), run: () => go(`${base}/pregled`) },
    { icon: 'pencil', label: tr('Izmeni', 'Edit', 'Изменить'), run: () => go(`${base}/izmeni`) },
    canPay && { icon: 'checkCircle', label: tr('Unesi uplatu', 'Record payment', 'Внести оплату'), run: startPay },
    { icon: 'fileText', label: tr('Napravi kopiju', 'Duplicate', 'Копировать'), run: () => go(`/fakturisanje/nova?kopija=${invoice.id}`) },
    { icon: 'download', label: tr('Preuzmi PDF', 'Download PDF', 'Скачать PDF'), run: () => go(`${base}/pregled?akcija=pdf`) },
    { icon: 'logout', label: tr('Pošalji fakturu', 'Send invoice', 'Отправить'), run: () => go(`${base}/pregled?akcija=posalji`) },
    (invoice.document_kind || 'faktura') === 'faktura' && !invoice.sef_invoice_id && { icon: 'globe', label: tr('Pošalji u SEF', 'Send to SEF', 'В SEF'), run: () => go(`${base}/pregled?akcija=sef`) },
    !isProforma && { icon: 'exchange', label: tr('Promeni status…', 'Change status…', 'Сменить статус…'), run: () => setView('status'), divider: true },
    canStorno && { icon: 'x', label: tr('Storniraj', 'Cancel invoice', 'Аннулировать'), run: () => go(`${base}/pregled?akcija=storno`), danger: true, divider: true },
    { icon: 'trash', label: tr('Obriši', 'Delete', 'Удалить'), run: () => go(`${base}/pregled?akcija=obrisi`), danger: true, divider: !canStorno },
  ].filter(Boolean);

  return (
    <div style={{ position: 'relative', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
      {canPay && (
        <button
          onClick={() => setPaying(true)}
          disabled={busy}
          className="st-icon-btn"
          style={{ ...iconBtn, background: 'var(--forest-100)', color: 'var(--forest-600)' }}
          title={tr('Unesi uplatu', 'Record payment', 'Внести оплату')}
          aria-label={tr('Unesi uplatu', 'Record payment', 'Внести оплату')}
        >
          <Icon name="checkCircle" size={18} />
        </button>
      )}
      <button
        ref={btnRef}
        onClick={openMenu}
        className="st-icon-btn"
        style={iconBtn}
        title={tr('Dodatne opcije', 'More options', 'Ещё')}
        aria-label={tr('Dodatne opcije', 'More options', 'Ещё')}
        aria-expanded={open}
      >
        <Icon name="moreVertical" size={18} />
      </button>

      {open && (
        <>
          <div style={backdrop} onClick={() => setOpen(false)} />
          <div style={{ ...menu, top: pos.top, left: pos.left }} role="menu">
            {view === 'actions' ? (
              actions.map((a) => (
                <div key={a.label}>
                  {a.divider && <div style={menuDivider} />}
                  <button
                    role="menuitem"
                    onClick={a.soon ? undefined : a.run}
                    disabled={a.soon}
                    style={{ ...menuItem, ...(a.danger ? { color: 'var(--red-600)' } : {}), ...(a.soon ? { color: 'var(--ink-300)', cursor: 'default' } : {}) }}
                  >
                    <Icon name={a.icon} size={16} />
                    <span style={{ flex: 1 }}>{a.label}</span>
                    {a.soon && <span style={soonTag}>{tr('Uskoro', 'Soon', 'Скоро')}</span>}
                  </button>
                </div>
              ))
            ) : (
              <>
                <button onClick={() => setView('actions')} style={{ ...menuTitle, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="arrowLeft" size={13} />{t('changeStatus')}
                </button>
                {STATUS_KEYS.map((k) => (
                  <button key={k} role="menuitem" onClick={() => pick(k)} disabled={busy} style={{ ...menuItem, ...(k === st ? menuItemOn : {}) }}>
                    <span style={{ ...dot, background: meta[k].dot }} />
                    <span style={{ flex: 1 }}>{meta[k].label}</span>
                    {k === st && <Icon name="check" size={15} color="var(--lavender-700)" strokeWidth={2.2} />}
                  </button>
                ))}
                {partial && (
                  <div style={partialBox}>
                    <label style={partialLabel}>{t('partialAmountLabel', cur)}</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input autoFocus type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') savePartial(); }} style={partialInput} />
                      <button onClick={savePartial} disabled={busy} style={partialSave}>{t('saveBtnShort')}</button>
                    </div>
                  </div>
                )}
              </>
            )}
            {error && <div style={errBox}>{error}</div>}
          </div>
        </>
      )}

      {paying && (
        <PaymentDialog
          invoice={invoice}
          onClose={() => setPaying(false)}
          onSaved={(d) => { setPaying(false); if (onChange) onChange(d); }}
        />
      )}
    </div>
  );
}

const menuDivider = { height: 1, background: 'var(--line)', margin: '4px 2px' };
const soonTag = { fontSize: 10.5, fontWeight: 700, color: 'var(--gold-600)', background: 'var(--gold-100)', borderRadius: 6, padding: '1px 6px' };
const pd = {
  overlay: { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(12,37,48,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, textAlign: 'left', cursor: 'default' },
  modal: { width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', background: 'var(--surface)', borderRadius: 10, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 20px 50px rgba(12,37,48,0.25)' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { flex: 1, fontSize: 19, fontWeight: 800, color: 'var(--ink-900)' },
  close: { width: 36, height: 36, border: 'none', background: 'transparent', color: 'var(--ink-500)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  docRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: -6 },
  docNo: { fontSize: 15, fontWeight: 800, color: 'var(--teal-800)' },
  curTag: { fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: 'var(--lavender-100)', color: 'var(--lavender-700)' },
  client: { fontSize: 13, color: 'var(--ink-500)' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12.5, fontWeight: 700, color: 'var(--ink-700)' },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 15, width: '100%', minHeight: 42, background: 'var(--surface)' },
  hint: { fontSize: 11.5, color: 'var(--ink-500)' },
  summary: { background: 'var(--paper)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 },
  sumTitle: { fontSize: 11.5, fontWeight: 800, letterSpacing: '0.06em', color: 'var(--ink-500)' },
  sumRow: { display: 'flex', justifyContent: 'space-between', gap: 10, color: 'var(--ink-700)' },
  sumLine: { height: 1, background: 'var(--line)' },
  btnRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 },
  cancel: { minHeight: 42, padding: '0 18px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--teal-800)', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  save: { minHeight: 42, padding: '0 20px', border: 'none', borderRadius: 8, background: 'var(--teal-800)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};

const badge = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, borderRadius: 8, padding: '4px 10px', whiteSpace: 'nowrap' };
const dot = { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 };
const iconBtn = { width: 34, height: 34, border: 'none', borderRadius: 10, background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' };
const backdrop = { position: 'fixed', inset: 0, zIndex: 40 };
const menu = { position: 'fixed', zIndex: 41, width: 240, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', padding: 8, textAlign: 'left' };
const menuTitle = { fontSize: 11, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 10px 8px' };
const menuItem = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 38, border: 'none', background: 'transparent', borderRadius: 9, padding: '0 10px', fontSize: 14, fontWeight: 500, color: 'var(--ink-900)', cursor: 'pointer', textAlign: 'left' };
const menuItemOn = { background: 'var(--lavender-100)', fontWeight: 700 };
const partialBox = { borderTop: '1px solid var(--line)', marginTop: 6, padding: '10px 6px 4px' };
const partialLabel = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 6 };
const partialInput = { flex: 1, minWidth: 0, border: '1.5px solid var(--line)', borderRadius: 8, padding: '8px 10px', fontSize: 14 };
const partialSave = { border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 12px', fontWeight: 700, fontSize: 13 };
const errBox = { margin: '8px 6px 2px', fontSize: 12, color: 'var(--red-600)' };
